import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Clock } from "lucide-react";
import TopBar from "@/components/TopBar";
import EventCountdown from "@/components/EventCountdown";
import EventActions from "@/components/EventActions";
import FrozenBanner from "@/components/FrozenBanner";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

const COVER_FALLBACK =
  "https://images.pexels.com/photos/37828118/pexels-photo-37828118.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function formatDate(iso, lang) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function EventDetail() {
  const { code } = useParams();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/public/events/${code}`);
        setEvent(data.event);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || t("code.error"));
      }
    };
    load();
  }, [code, t]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF9FB]">
        <TopBar />
        <div className="max-w-md mx-auto px-5 mt-20 text-center">
          <p className="text-[#D81B8C] font-semibold" data-testid="event-error">
            {error}
          </p>
          <Button onClick={() => navigate("/code")} className="mt-6 rounded-full bg-[#D81B8C] hover:bg-[#A60F6E]">
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#FFF9FB] flex items-center justify-center text-[#5C4664]">
        {t("common.loading")}
      </div>
    );
  }

  const cover = event.cover_image_url || COVER_FALLBACK;

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24">
      {/* Cover */}
      <div className="relative h-[44vh] sm:h-[52vh] w-full overflow-hidden">
        <img src={cover} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 milk-hero-vignette" />
        <TopBar variant="transparent" />
        <button
          onClick={() => navigate("/")}
          data-testid="event-back"
          className="absolute top-4 left-4 z-20 w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white hover:bg-white/25"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Event card */}
      <div className="max-w-2xl mx-auto px-5 -mt-20 sm:-mt-24 relative z-10">
        <div className="bg-white rounded-3xl p-7 sm:p-9 border border-[#F2E2EC] shadow-xl shadow-black/5">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#D81B8C]">
            {t("event.hostedBy")}
          </p>
          <h1 data-testid="event-host-names" className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] mt-1 leading-tight">
            {event.host_names}
          </h1>
          <p className="mt-2 text-[#5C4664]" data-testid="event-title">{event.title}</p>
          {event.description ? (
            <p className="mt-4 text-sm text-[#5C4664] leading-relaxed">{event.description}</p>
          ) : null}

          <EventCountdown
            targetDate={
              new Date(`${event.event_date}T${(event.event_time || "00:00")}:00`)
            }
          />

          <div className="mt-6 space-y-4 text-sm">
            <div className="flex items-start gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <Calendar className="w-4 h-4 mt-0.5 text-[#D81B8C] shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#5C4664]">
                    {t("event.whenWhere")}
                  </div>
                  <div className="text-[#2A0E3C] font-medium" data-testid="event-date">
                    {formatDate(event.event_date, lang)}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 shrink-0">
                <Clock className="w-4 h-4 mt-0.5 text-[#D81B8C]" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-[#5C4664]">
                    {t("event.time")}
                  </div>
                  <div className="text-[#2A0E3C] font-medium">{event.event_time}</div>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#D81B8C] shrink-0" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-[#5C4664]">
                  {t("event.address")}
                </div>
                <div className="text-[#2A0E3C] font-medium">{event.venue}</div>
                <div className="text-[#5C4664]">{event.address}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Frozen-purchases banner (J-48h) */}
        <FrozenBanner event={event} />

        {/* 6 action buttons */}
        <EventActions event={event} />
      </div>
    </div>
  );
}
